import { action, computed } from 'mobx';

import { SubstitutionCompareCard } from './SubstitutionCompareCard';
import * as constants from '../../../constants/constants';
import { parseFloatI18n } from "@pes-ui/utils";
import { send, removeFromFavoritesEvent, isAnalyticsEnabled ,addToCartSubstitution} from '@pes-ui/utils';
import {getProductsQuantityMap} from '@pes-ui/components/dist/collection/components/product-description-page/substitutions-or-alternatives/substitutions/utils'
import {sendAnalytics} from '@pes-ui/components/dist/collection/components/product-description-page/substitutions-or-alternatives/utils'

/**
 * @typedef {import('../../../types').ProductData} ProductData
 * @typedef {import('../../../types').MyProductsAPI} MyProductsAPI
 */

export const getProductFromList = (response) => {
    const singleGroup = response.lists && response.lists[0];
    let productObj;

    if (singleGroup) {
        productObj = singleGroup.products && singleGroup.products[0];
    }

    return productObj;
};

export class SubstitutionSectionStore {
    card = null;

    pendingReaction = false;

    constructor(parent, substObj, rootStore) {
        this.sectionType = substObj.type.toLowerCase();
        this.isProduct = this.sectionType === 'product';
        this.isGroup = this.sectionType === constants.groupType.GROUP;
        this.products = substObj.products;
        this.range = substObj.range;
        this.title = substObj.title;
        this.configurationMasterId = String(substObj.substitutionId);
        this.commerceRef = this.isProduct ? this.products[0].sku.commercialReference : '';
        this.replacingQty = this.isProduct ? this.products[0].quantity : 1;
        this.comparableCard = this.isProduct ? new SubstitutionCompareCard(this.commerceRef, rootStore) : null;
        this.cartStore = rootStore.cartStore;
        this.labelsStore = rootStore.labelsStore;
        this.obsoleteProductCard = parent;
        /** @type {MyProductsAPI} */
        this.myProductsAPI = rootStore.services.myProductsAPI;
    }

    @computed get isFavorite() {
        return this.checkIsFavorite();
    }

    checkIsFavorite() {
        if (this.isGroup) {
            this.card = this.cartStore.getProductGroupCardById(this.configurationMasterId);
        } else {
            const card = this.cartStore.getProductCardById(this.commerceRef);

            this.card = card && (card.quantity < this.replacingQty) ? null : card;
        }

        return !!this.card;
    }

    toggleFavorite() {
        if (this.isFavorite) {
            this.removeFromFav();
        } else {
            this.addToFav();
        }
    }

    addToCart() {
        if (this.isFavorite) {
            this.changeCardQty();
            return;
        }

        this.addToFav();
    }

    @action.bound
    removeFromFav() {
        this.card.remove();
    }

    @action.bound
    addToFav() {
        if(this.pendingReaction || this.addQtyToSubstSingle()) {
            return;
        }

        const [targetGroup] = this.cartStore.getGroupsByType(constants.groupType.SINGLE);

        if (targetGroup) {
            let dataToAdd;
            let postDataToAdd;

            if (this.isGroup) {
                dataToAdd = this.getGroupData();
                postDataToAdd = () => this.addGroupToFav(dataToAdd);
            } else if (this.isProduct) {
                dataToAdd = this.getProductData();
                postDataToAdd = () => this.addProductToFav(dataToAdd);
            }

            if (dataToAdd) {
                this.pendingReaction = true;

                postDataToAdd()
                    .finally(() => { this.pendingReaction = false; });
            }
        }
    }

    publishAddToCartAnalyticsEvent(myProductsPostResponse) {
      const productsQuantityMap = this.getProductsQuantityMap();
      sendAnalytics(
        myProductsPostResponse,
        this.obsoleteProductCard.skuID,
        productsQuantityMap,
        false
      );
    }

    async addGroupToFav(dataToAdd) {
      try {
        const myProductsPostResponse = await this.myProductsAPI.addProducts(
          dataToAdd
        );
        const repositoryId = myProductsPostResponse?.groupId;
        const myProductsGetResponse = await this.myProductsAPI.getProductsFullData();
        const selectedItem = myProductsGetResponse?.lists?.find(
          (product) => product.repositoryId === repositoryId
        );
        this.cartStore.addGroup({
          groupType: constants.substitute,
          item: selectedItem,
        });
        if (!isAnalyticsEnabled()) return;  
        this.publishAddToCartAnalyticsEvent(myProductsPostResponse);
      } catch (error) {
        console.error(error);
      }
    }
    
  /** @param {ProductData} dataToAdd */
  async addProductToFav(dataToAdd) {
    try {
      const myProductsPostResponse = await this.myProductsAPI.addProducts(
        dataToAdd
      );
      if (!myProductsPostResponse) return;
      const myProductsGetResponse = await this.myProductsAPI.getProductsFullData();
      const product = getProductFromList(myProductsGetResponse);
      product.price = parseFloatI18n(product.price);
      this.cartStore.addProductToGroup(product);
      if (!isAnalyticsEnabled()) return; 
      this.publishAddToCartAnalyticsEvent(myProductsPostResponse);
    } catch (error) {
      console.error(error);
    }
  }

    getSubstSingleFromCart() {
        const card = this.cartStore.getProductCardById(this.commerceRef);

        return card && card.replacing ? card : null;
    }

    changeCardQty() {
        const qtyToAdd = this.replacingQty * this.obsoleteProductCard.quantity;
        this.card.changeQty(qtyToAdd);
        const {brand ,ID,commDesc,currency,price} = this.card
        const product = {
          brand: brand,
          price,
          category: '',
          id:ID,
          name:commDesc,
          quantity: 1,
          variant: this.obsoleteProductCard.skuID
        }
        send(addToCartSubstitution, {
          currencyCode: currency,
          distributor: '',
          products: [product],
          isSticky: false
        });
    }

    addQtyToSubstSingle() {
        if (this.isProduct) {
            this.card = this.getSubstSingleFromCart();

            if (this.card) {
                this.changeCardQty();
            }
        }

        return !!this.card;
    }

    /** @returns {ProductData} */
    getProductData() {
        return {
            productId: this.commerceRef,
            productQty: this.replacingQty * this.obsoleteProductCard.quantity,
            parentId: this.obsoleteProductCard.skuID,
            replacingQty: this.replacingQty
        };
    }
    
    getProductsQuantityMap(){
      return this.products.reduce((result, product) => {
        result[product.sku.commercialReference] = product.quantity;
    
        return result;
      }, {});
    }
    /** @returns {ProductData} */
    getGroupData() {
        const productIds = [];
        const quantities = [];
        this.products.forEach((product) => {
            productIds.push(product.sku.commercialReference);
            quantities.push(product.quantity);
        });

        const pattern = /#commercial-reference#/i;
        const groupNameTemplate = this.labelsStore.substSectionGroupTitle;
        const displayName = groupNameTemplate.replace(pattern, this.obsoleteProductCard.ID);

        return {
            productId: productIds,
            productQty: quantities,
            groupType: 'substitute',
            groupId: this.configurationMasterId,
            displayName
        };
    }
}
